'use client';
import { useEffect, useRef } from 'react';
/**
 * Outside-click / Escape dismiss for a popover. Extracted from the six
 * byte-/near-identical copies across the copilot menus, placement menu,
 * session picker, and header dropdown (deep-code-analysis §3). While `open`,
 * a document-level pointer listener closes the popover on any click outside
 * `ref`, and a keydown listener closes it on Escape. Both are torn down when
 * the popover closes or the component unmounts.
 *
 * The latest `onDismiss` is read through a ref so a fresh closure on every
 * render does not re-subscribe the listeners — the subscription lifecycle
 * matches the original effects, which depended only on `open`.
 */
export function useDismissablePopover(ref, open, onDismiss, opts) {
    const pointerEvent = opts?.pointerEvent ?? 'mousedown';
    const onDismissRef = useRef(onDismiss);
    onDismissRef.current = onDismiss;
    useEffect(() => {
        if (!open)
            return;
        function onPointer(event) {
            if (ref.current && !ref.current.contains(event.target))
                onDismissRef.current();
        }
        function onKey(event) {
            if (event.key === 'Escape')
                onDismissRef.current();
        }
        document.addEventListener(pointerEvent, onPointer);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener(pointerEvent, onPointer);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, pointerEvent, ref]);
}
//# sourceMappingURL=use-dismissable-popover.js.map